import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { SxProps, Theme } from "@mui/material/styles";
import type { SortableColumn } from "../../components/common/SortableTable";
import type { ConflictBookingGroup } from "./refundConflictUtils";
import type { RefundRow } from "./refundTypes";

const HEADER_CELL_SX = {
  backgroundColor: "rgba(191, 229, 255, 1) !important",
  position: "sticky" as const,
  top: 0,
  zIndex: 1,
};

const GROUP_ROW_SX = {
  backgroundColor: "rgba(232, 245, 255, 0.95)",
  "& > td": { fontWeight: 600, borderBottom: "1px solid rgba(144, 202, 249, 0.5)" },
  "&:hover": { backgroundColor: "rgba(207, 236, 255, 1) !important" },
};

export interface RefundConflictBookingTableProps {
  groups: ConflictBookingGroup<RefundRow>[];
  columns: SortableColumn<RefundRow>[];
  expandedBookingIds: Set<string>;
  onToggleExpand: (bookingMasterId: string) => void;
  getRowProps?: (row: RefundRow) => { sx?: SxProps<Theme>; title?: string } | undefined;
  emptyMessage?: React.ReactNode;
  maxHeight?: number;
}

const REFUND_ACTION_COLUMN_LABEL = "Action";

function isRefundActionColumn(column: SortableColumn<RefundRow>): boolean {
  return column.label === REFUND_ACTION_COLUMN_LABEL;
}

function renderCell(column: SortableColumn<RefundRow>, row: RefundRow): React.ReactNode {
  if (column.render) return column.render(row);
  const value = row[column.id];
  if (value == null || value === "") return "—";
  return String(value);
}

const RefundConflictBookingTable = ({
  groups,
  columns,
  expandedBookingIds,
  onToggleExpand,
  getRowProps,
  emptyMessage = "No conflict refunds found for the current filters",
  maxHeight = 500,
}: RefundConflictBookingTableProps) => {
  if (groups.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        {typeof emptyMessage === "string" ? (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : (
          emptyMessage
        )}
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight, overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} aria-label="Expand" />
            {columns.map((column) => (
              <TableCell
                key={String(column.id)}
                align={column.align || "left"}
                sx={HEADER_CELL_SX}
              >
                <Typography variant="body2" className="fw-bold">
                  {column.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const expanded = expandedBookingIds.has(group.bookingMasterId);
            const primary = group.refunds[0];
            if (!primary) return null;

            return (
              <React.Fragment key={group.bookingMasterId}>
                <TableRow
                  hover
                  sx={{ ...GROUP_ROW_SX, cursor: "pointer" }}
                  onClick={() => onToggleExpand(group.bookingMasterId)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      aria-label={
                        expanded
                          ? `Collapse conflicts for booking ${group.bookingMasterId}`
                          : `Expand conflicts for booking ${group.bookingMasterId}`
                      }
                      aria-expanded={expanded}
                      onClick={() => onToggleExpand(group.bookingMasterId)}
                      sx={{
                        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={700}>
                        {group.bookingMasterId}
                      </Typography>
                      <Chip
                        label={`${group.refunds.length} conflict${group.refunds.length === 1 ? "" : "s"}`}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    </Box>
                  </TableCell>
                  {columns.slice(1).map((column) => {
                    if (column.id === "isAddedByWeb") {
                      const manualCount = group.refunds.filter((r) => r.isAddedByWeb).length;
                      const systemCount = group.refunds.length - manualCount;
                      return (
                        <TableCell key={String(column.id)} align={column.align || "left"}>
                          <Typography variant="caption" color="text.secondary">
                            {manualCount > 0 ? `${manualCount} manual` : ""}
                            {manualCount > 0 && systemCount > 0 ? " · " : ""}
                            {systemCount > 0 ? `${systemCount} system` : ""}
                          </Typography>
                        </TableCell>
                      );
                    }
                    if (isRefundActionColumn(column)) {
                      return (
                        <TableCell key={String(column.id)} align="center">
                          <Typography variant="caption" color="text.secondary">
                            Expand to view
                          </Typography>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={String(column.id)} align={column.align || "left"}>
                        {renderCell(column, primary)}
                      </TableCell>
                    );
                  })}
                </TableRow>

                {expanded &&
                  group.refunds.map((refund) => {
                    const rowProps = getRowProps?.(refund);
                    return (
                      <TableRow
                        key={`${group.bookingMasterId}-${String(refund.refundId)}`}
                        hover
                        title={rowProps?.title}
                        sx={{
                          ...rowProps?.sx,
                          "& > td:first-of-type": { pl: 6 },
                        }}
                      >
                        <TableCell />
                        {columns.map((column) => {
                          if (column.id === "bookingMasterId") {
                            return (
                              <TableCell key={String(column.id)}>
                                <Typography variant="body2" color="text.secondary">
                                  Refund #{String(refund.refundId)}
                                </Typography>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell
                              key={String(column.id)}
                              align={column.align || "left"}
                              onClick={(e) => {
                                if (isRefundActionColumn(column)) e.stopPropagation();
                              }}
                            >
                              {renderCell(column, refund)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RefundConflictBookingTable;
